import { ExSideDrawer } from '@boomi/exosphere';
import { type ComponentRef, useCallback, useEffect, useRef, useState } from 'react';
import { useDrawerHeaderOffset } from '../app-shell/headerHeight';
import IdentityProviderCreate from './forms/IdentityProviderCreate';
import IdentityProviderEdit from './forms/IdentityProviderEdit';
import styles from './IdentityProviderDrawer.module.css';

export type IdentityProviderDrawerMode =
    | { type: 'create-idp' }
    | { type: 'edit-idp'; identityProviderId: string };

interface IdentityProviderDrawerProps {
    mode: IdentityProviderDrawerMode | null;
    onClose: () => void;
}

const IdentityProviderDrawer = ({ mode, onClose }: IdentityProviderDrawerProps) => {
    const [isOpen, setIsOpen] = useState(mode !== null);
    const drawerRef = useRef<ComponentRef<typeof ExSideDrawer>>(null);
    const headerHeight = useDrawerHeaderOffset(drawerRef);

    useEffect(() => {
        setIsOpen(mode !== null);
    }, [mode]);

    const handleClose = useCallback(() => {
        setIsOpen(false);
        onClose();
    }, [onClose]);

    useEffect(() => {
        if (mode !== null) {
            document.documentElement.style.overflow = 'hidden';
            document.documentElement.scrollTop = 0;
        }

        return () => {
            document.documentElement.style.overflow = 'auto';
        };
    }, [mode]);

    useEffect(() => {
        if (!isOpen) return;
        const handleOutsideClick = (e: MouseEvent) => {
            const drawer = drawerRef.current;
            if (drawer && !e.composedPath().includes(drawer)) {
                handleClose();
            }
        };
        document.addEventListener('mousedown', handleOutsideClick);
        return () => document.removeEventListener('mousedown', handleOutsideClick);
    }, [isOpen, handleClose]);

    return (
        <ExSideDrawer
            ref={drawerRef}
            open={isOpen}
            top={headerHeight}
            width="default"
            noMarginSlot
            panelTitle={''}
            onClose={handleClose}
            className={styles.identityProviderDrawer}
        >
            {mode?.type === 'create-idp' && <IdentityProviderCreate onClose={handleClose} />}
            {mode?.type === 'edit-idp' && (
                <IdentityProviderEdit
                    identityProviderId={mode.identityProviderId}
                    onClose={handleClose}
                />
            )}
        </ExSideDrawer>
    );
};

export default IdentityProviderDrawer;
